import { useCallback, useEffect, useState } from 'react';

import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Typography,
} from '@mui/material';

import { ExitCriteriaFields, Send } from 'domains/campaigns/types';
import Autocomplete from 'domains/core/components/Autocomplete';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import Button from 'domains/core/components/Button';
import ConfirmationDialog, { DialogProps } from 'domains/core/components/ConfirmationDialog/ConfirmationDialog';
import NestedFilterBuilder from 'domains/segments/components/NestedFilterBuilder';
import { NestedSegmentDefinitionInput, Segment, SegmentDefinition } from 'domains/segments/types';
import useSegmentPreviewCount from 'hooks/mutations/useSegmentPreviewCount';
import useSegment from 'hooks/queries/useSegment';
import { getNumberWithCommas } from 'utils';

import { buildScopedExitCountDefinition } from './helpers';

type ExitCriteriaMode = 'saved' | 'custom';

// A stored definition that carries a segmentId is a snapshot of a saved audience (picked via the
// dropdown). One without an id was built inline with the custom-rules builder. We use this to default
// the mode sensibly on edit.
export const getInitialMode = (exitCriteria?: ExitCriteriaFields | null): ExitCriteriaMode =>
    exitCriteria && !exitCriteria.segmentId ? 'custom' : 'saved';

export type ExitCriteriaDialogProps = {
    isOpen: boolean;
    initialCriteria?: ExitCriteriaFields | null;
    segments?: Segment[];
    isSegmentsLoading: boolean;
    campaignSends?: Send[];
    /**
     * The campaign's starting-audience definition. When provided, the matching-contact count is scoped
     * to the starting audience (entry ∩ exit); when omitted/null the count is hidden entirely.
     */
    startingAudienceDefinition?: SegmentDefinition | null;
    /** Dialog heading. Defaults to "Define exit criteria" (builder); the live panel uses "Edit exit criteria". */
    title?: string;
    /**
     * Render an on/off toggle inside the modal so the whole feature can be enabled or disabled from one
     * place: off hides the rules and Save persists null (clear). Used by the live details panel.
     */
    showEnableToggle?: boolean;
    /**
     * Gate Save behind a confirmation dialog. Used on the live panel, where applying persists immediately
     * to a running campaign.
     */
    confirmOnSave?: boolean;
    onClose: () => void;
    onSave: (definition: ExitCriteriaFields | null) => void;
};

/**
 * Authoring surface for the exit criteria, shown in a modal. Holds its own DRAFT of the definition
 * (seeded from the committed criteria) and only commits via onSave — Cancel discards. Offers two modes:
 * pick a saved audience (the dropdown seeds the draft with that audience's definition) or build custom
 * rules inline. Engagement send pickers are fed the campaign's own sends via campaignSends so the rules
 * can reference this campaign's sends, exactly like conditional splits.
 *
 * On the live details panel (showEnableToggle + confirmOnSave) the dialog also owns the on/off toggle
 * and gates Save behind a confirmation, so enabling, editing, and clearing all happen in one modal.
 */
const ExitCriteriaDialog = ({
    isOpen,
    initialCriteria,
    segments,
    isSegmentsLoading,
    campaignSends,
    startingAudienceDefinition,
    title = 'Define exit criteria',
    showEnableToggle = false,
    confirmOnSave = false,
    onClose,
    onSave,
}: ExitCriteriaDialogProps) => {
    const [enabled, setEnabled] = useState(Boolean(initialCriteria));
    const [mode, setMode] = useState<ExitCriteriaMode>(getInitialMode(initialCriteria));
    const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(initialCriteria?.segmentId ?? null);
    const [draft, setDraft] = useState<ExitCriteriaFields | null>(initialCriteria ?? null);
    // Snapshot of the definition to seed the custom-rules builder on first render only (so subsequent
    // edits aren't fought by a re-prefill). Captured when custom mode is the initial mode.
    const [customSeed] = useState<ExitCriteriaFields | null>(() =>
        getInitialMode(initialCriteria) === 'custom' ? initialCriteria ?? null : null
    );
    const [isCustomValid, setIsCustomValid] = useState(
        Boolean(initialCriteria) && getInitialMode(initialCriteria) === 'custom'
    );
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    // Fetch the seed audience's full definition, then snapshot it into the draft (stamping the
    // segmentId so the summary can resolve the audience name later).
    const { isFetching: isSegmentFetching, isError: isSegmentError } = useSegment(selectedSegmentId, {
        enabled: Boolean(selectedSegmentId),
        onSuccess: (segment) => {
            setDraft({ ...segment.segmentDefinition, segmentId: selectedSegmentId ?? undefined });
        },
    });

    const {
        mutate: previewCount,
        data: countData,
        isLoading: isCountLoading,
        isError: isCountError,
    } = useSegmentPreviewCount();

    // The rules section is shown unless the in-modal toggle is present and switched off.
    const rulesVisible = !showEnableToggle || enabled;

    // Scope the matching-contact count to the campaign's starting audience (entry ∩ exit). When the
    // starting-audience definition isn't available (cohort / A-B / continual entry), we don't preview
    // a count — a whole-universe number is misleading for a small enrolled audience.
    const canScopeCount = startingAudienceDefinition != null;
    useEffect(() => {
        if (draft && rulesVisible && canScopeCount) {
            previewCount(buildScopedExitCountDefinition(startingAudienceDefinition, draft));
        }
    }, [draft, rulesVisible, canScopeCount, startingAudienceDefinition, previewCount]);

    const segmentOptions: AutocompleteOption<number>[] = (segments ?? []).map((segment) => ({
        label: segment.name,
        value: segment.id,
    }));
    const selectedOption = segmentOptions.find((option) => option.value === selectedSegmentId) ?? null;

    const handleModeChange = (nextMode: ExitCriteriaMode) => {
        if (nextMode === mode) return;
        setMode(nextMode);
        // Switching modes discards the previously defined criteria so the two modes never overlap.
        setSelectedSegmentId(null);
        setIsCustomValid(false);
        setDraft(null);
    };

    const handleSegmentChange = (option: AutocompleteOption<number> | null) => {
        const segmentId = option?.value ?? null;
        setSelectedSegmentId(segmentId);
        // The definition is set in useSegment's onSuccess once it has been fetched.
        if (!segmentId) setDraft(null);
    };

    const handleCustomChange = useCallback((definition: NestedSegmentDefinitionInput, isValid: boolean) => {
        setIsCustomValid(isValid);
        setDraft(isValid ? (definition as ExitCriteriaFields) : null);
    }, []);

    // What Save will persist: null when the in-modal toggle is off (clear), otherwise the draft.
    const resultDefinition = showEnableToggle && !enabled ? null : draft;
    const isSaveable =
        showEnableToggle && !enabled ? true : mode === 'custom' ? isCustomValid && Boolean(draft) : Boolean(draft);

    const matchCount = countData?.count;
    const isCountFetching = isSegmentFetching || isCountLoading;
    const isCountErrored = isSegmentError || isCountError;

    const commit = () => {
        setIsConfirmOpen(false);
        onSave(resultDefinition);
    };

    const handleSaveClick = () => {
        if (confirmOnSave) setIsConfirmOpen(true);
        else commit();
    };

    // Confirmation copy adapts to the action being applied to the live campaign.
    const confirmDialogProps: DialogProps =
        resultDefinition == null
            ? {
                  title: 'Clear exit criteria?',
                  contentText:
                      'Contacts will no longer exit this Curation based on these rules. Contacts that have already exited stay exited.',
                  confirmButtonText: 'Clear',
              }
            : {
                  title: initialCriteria ? 'Update exit criteria?' : 'Turn on exit criteria?',
                  contentText:
                      'These rules take effect from the next send onward — matching contacts exit and receive no further sends. Contacts that have already exited stay exited.',
                  confirmButtonText: initialCriteria ? 'Update' : 'Turn on',
              };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                aria-labelledby="exit-criteria-dialog-title"
                data-testid="exit-criteria-dialog"
            >
                <DialogTitle id="exit-criteria-dialog-title">{title}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} marginTop="4px">
                        {showEnableToggle && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        color="secondary"
                                        size="small"
                                        checked={enabled}
                                        onChange={(e) => setEnabled(e.target.checked)}
                                        inputProps={{ 'aria-label': 'Enable campaign exit criteria' }}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        Contacts matching an audience exit this Curation
                                    </Typography>
                                }
                            />
                        )}
                        {rulesVisible && (
                            <>
                                <RadioGroup
                                    row
                                    aria-label="Exit criteria mode"
                                    value={mode}
                                    onChange={(e) => handleModeChange(e.target.value as ExitCriteriaMode)}
                                >
                                    <FormControlLabel
                                        data-testid="exit-mode-saved"
                                        value="saved"
                                        control={<Radio size="small" color="secondary" />}
                                        label={<Typography variant="body2">Use a saved audience</Typography>}
                                    />
                                    <FormControlLabel
                                        data-testid="exit-mode-custom"
                                        value="custom"
                                        control={<Radio size="small" color="secondary" />}
                                        label={<Typography variant="body2">Create custom rules</Typography>}
                                    />
                                </RadioGroup>
                                {mode === 'saved' ? (
                                    <Autocomplete
                                        getOptionLabel={(option) => option.label ?? ''}
                                        label="Exit audience"
                                        loading={isSegmentsLoading}
                                        onChange={(_e: any, option: AutocompleteOption<number> | null) =>
                                            handleSegmentChange(option)
                                        }
                                        options={segmentOptions}
                                        placeholder="Select an audience"
                                        type="string"
                                        value={selectedOption}
                                    />
                                ) : (
                                    // Bottom padding so the builder's Add filter / Add filter group buttons
                                    // aren't flush against the dialog actions.
                                    <Box sx={{ pb: 2 }}>
                                        <NestedFilterBuilder
                                            initialDefinition={customSeed}
                                            onChange={handleCustomChange}
                                            additionalSends={campaignSends}
                                        />
                                    </Box>
                                )}
                                {draft && canScopeCount && (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {isCountFetching ? (
                                            <CircularProgress
                                                size={14}
                                                role="progressbar"
                                                aria-label="Loading exit count"
                                            />
                                        ) : isCountErrored ? (
                                            <Typography variant="caption" color="error.main">
                                                Couldn’t load the matching contact count.
                                            </Typography>
                                        ) : (
                                            matchCount != null && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    data-testid="exit-criteria-dialog-count"
                                                >
                                                    ~{getNumberWithCommas(Number(matchCount))} of your starting audience
                                                    match this exit criteria.
                                                </Typography>
                                            )
                                        )}
                                    </Stack>
                                )}
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={onClose} data-testid="exit-criteria-cancel">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!isSaveable}
                        onClick={handleSaveClick}
                        data-testid="exit-criteria-save"
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
            {isConfirmOpen && (
                <ConfirmationDialog
                    open={isConfirmOpen}
                    dialogProps={confirmDialogProps}
                    onConfirm={commit}
                    onCancel={() => setIsConfirmOpen(false)}
                    onModalClose={() => setIsConfirmOpen(false)}
                />
            )}
        </>
    );
};

export default ExitCriteriaDialog;
